import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Check, Copy, Instagram, Globe, Link2, Image, QrCode } from 'lucide-react';
import { track } from '@/lib/analytics';
import { getPriceCopy } from '@/lib/events/priceCopy';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

type SharePlatform = 'instagram' | 'facebook' | 'bio';
type TextVariant = 'short' | 'detailed';

interface ShareKitProps {
  eventId: string;
  eventTitle: string;
  eventSlug?: string | null;
  producerId: string;
  producerSlug?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  isFree: boolean;
  priceOre: number;
  bookingMode: string;
  timingMode?: string;
}

const PLATFORM_LABELS: Record<SharePlatform, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  bio: 'Länk i bio',
};

const VARIANT_LABELS: Record<TextVariant, string> = {
  short: 'Kort',
  detailed: 'Mer info',
};

const BASE_URL = 'https://www.farmly.se';

function buildUtmUrl(
  eventId: string,
  eventSlug: string | null | undefined,
  platform: SharePlatform,
  producerSlug: string | null | undefined,
): string {
  const params = new URLSearchParams({
    utm_source: platform,
    utm_medium: 'social',
    utm_campaign: producerSlug || 'unknown',
    utm_content: eventSlug || eventId,
  });
  return `${BASE_URL}/event/${eventId}?${params.toString()}`;
}

function formatDateRange(startDate: string | null | undefined): string | null {
  if (!startDate) return null;
  try {
    const start = new Date(startDate);
    return format(start, "d MMMM yyyy 'kl' HH:mm", { locale: sv });
  } catch {
    return null;
  }
}

function generateSocialText(
  platform: SharePlatform,
  variant: TextVariant,
  props: Pick<ShareKitProps, 'eventTitle' | 'startDate' | 'endDate' | 'isFree' | 'priceOre' | 'bookingMode' | 'timingMode'>,
): string {
  const lines: string[] = [];

  lines.push(props.eventTitle);
  lines.push('');

  // Date
  const dateStr = formatDateRange(props.startDate);
  if (dateStr) {
    lines.push(`📅 ${dateStr}`);
  } else if (props.timingMode === 'open' || props.timingMode === 'anytime') {
    lines.push('📅 Öppet under säsong');
  } else if (props.timingMode === 'season') {
    lines.push('📅 Säsongsaktivitet');
  }

  // Price
  const priceCopy = getPriceCopy({ is_free: props.isFree, price_ore: props.priceOre });
  if (priceCopy.primary) lines.push(`💰 ${priceCopy.primary}`);

  // Detailed variant adds extra context
  if (variant === 'detailed') {
    lines.push('');
    if (props.bookingMode === 'required') {
      lines.push('Antal platser är begränsat – boka i god tid.');
    } else if (props.bookingMode === 'optional') {
      lines.push('Skicka gärna en intresseanmälan så vet vi att du kommer.');
    } else {
      lines.push('Välkommen att titta in när det passar dig!');
    }
    if (priceCopy.secondary) {
      lines.push(priceCopy.secondary);
    }
  }

  // CTA
  lines.push('');
  if (props.bookingMode === 'required') {
    lines.push('Boka din plats via länken! 👇');
  } else if (props.bookingMode === 'optional') {
    lines.push('Anmäl ditt intresse via länken! 👇');
  } else {
    lines.push('Läs mer via länken! 👇');
  }

  // Platform-specific suffix
  if (platform === 'instagram') {
    lines.push('');
    lines.push('🔗 Länk i bio');
  }

  return lines.join('\n');
}

export function ShareKit(props: ShareKitProps) {
  const [platform, setPlatform] = useState<SharePlatform>('instagram');
  const [variant, setVariant] = useState<TextVariant>('short');
  const [copiedField, setCopiedField] = useState<'text' | 'link' | null>(null);

  const utmUrl = buildUtmUrl(props.eventId, props.eventSlug, platform, props.producerSlug);
  const socialText = generateSocialText(platform, variant, props);

  const handlePlatformChange = useCallback((value: string) => {
    if (!value) return;
    const p = value as SharePlatform;
    setPlatform(p);
    track('share_platform_selected', {
      activity_id: props.eventId,
      producer_id: props.producerId,
      platform: p,
    });
  }, [props.eventId, props.producerId]);

  const handleCopy = useCallback(async (field: 'text' | 'link') => {
    const content = field === 'text' ? socialText : utmUrl;
    try {
      await navigator.clipboard.writeText(content);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success(field === 'text' ? 'Text kopierad!' : 'Länk kopierad!');
      track('share_copy_clicked', {
        activity_id: props.eventId,
        producer_id: props.producerId,
        platform,
        kind: field,
      });
    } catch {
      toast.error('Kunde inte kopiera. Försök markera och kopiera manuellt.');
    }
  }, [socialText, utmUrl, props.eventId, props.producerId, platform]);

  // Track open on first render
  const [tracked, setTracked] = useState(false);
  if (!tracked) {
    setTracked(true);
    track('share_kit_opened', {
      activity_id: props.eventId,
      producer_id: props.producerId,
    });
  }

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium">Dela på sociala medier</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Platform selector */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Plattform</Label>
          <ToggleGroup
            type="single"
            value={platform}
            onValueChange={handlePlatformChange}
            className="justify-start"
          >
            <ToggleGroupItem value="instagram" aria-label="Instagram" className="gap-1.5 text-xs">
              <Instagram className="h-3.5 w-3.5" />
              {PLATFORM_LABELS.instagram}
            </ToggleGroupItem>
            <ToggleGroupItem value="facebook" aria-label="Facebook" className="gap-1.5 text-xs">
              <Globe className="h-3.5 w-3.5" />
              {PLATFORM_LABELS.facebook}
            </ToggleGroupItem>
            <ToggleGroupItem value="bio" aria-label="Länk i bio" className="gap-1.5 text-xs">
              <Link2 className="h-3.5 w-3.5" />
              {PLATFORM_LABELS.bio}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {/* Text variant selector */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Textlängd</Label>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => handleCopy('text')}
            >
              {copiedField === 'text' ? (
                <Check className="h-3 w-3" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
              Kopiera text
            </Button>
          </div>
          <ToggleGroup
            type="single"
            value={variant}
            onValueChange={(v) => v && setVariant(v as TextVariant)}
            className="justify-start"
          >
            <ToggleGroupItem value="short" className="text-xs">
              {VARIANT_LABELS.short}
            </ToggleGroupItem>
            <ToggleGroupItem value="detailed" className="text-xs">
              {VARIANT_LABELS.detailed}
            </ToggleGroupItem>
          </ToggleGroup>
          <Textarea
            value={socialText}
            readOnly
            rows={variant === 'detailed' ? 10 : 7}
            className="text-sm font-mono resize-none bg-muted/30"
          />
        </div>

        {/* UTM Link */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Spårad länk</Label>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => handleCopy('link')}
            >
              {copiedField === 'link' ? (
                <Check className="h-3 w-3" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
              Kopiera länk
            </Button>
          </div>
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
            <p className="text-xs font-mono text-muted-foreground break-all select-all">
              {utmUrl}
            </p>
          </div>
        </div>

        {/* Social Assets */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Visuella tillgångar</Label>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs"
              asChild
            >
              <a
                href={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/social-card?event_id=${props.eventId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image className="h-3.5 w-3.5" />
                Social-bild
              </a>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs"
              asChild
            >
              <a
                href={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/qr-code?event_id=${props.eventId}&producer_slug=${props.producerSlug || 'unknown'}&event_slug=${props.eventSlug || props.eventId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <QrCode className="h-3.5 w-3.5" />
                QR-kod
              </a>
            </Button>
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Länken spårar besök från {PLATFORM_LABELS[platform]} via UTM-parametrar.
        </p>
      </CardContent>
    </Card>
  );
}
